import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { DeleteConfirmation } from '@/shared/components/crud/delete-confirmation'
import { bankAccountsApi } from '../api/bank-accounts-api'
import { AccountDeleteErrorCode, readAccountDeleteError } from '../lib/account-delete-error'
import type { BankAccount } from '../types/bank-account'

interface AccountDeleteConfirmationProps {
  account: BankAccount | null
  onOpenChange: (open: boolean) => void
}

/** Confirms and deletes a bank account, explaining a failure by the backend's errorCode (UC-079). */
export function AccountDeleteConfirmation({ account, onOpenChange }: AccountDeleteConfirmationProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const deleteMutation = bankAccountsApi.useDelete()

  const handleConfirm = async () => {
    if (!account) return
    try {
      await deleteMutation.mutateAsync(account.id)
      toast.success('Account deleted')
      onOpenChange(false)
    } catch (error) {
      const { code, message } = readAccountDeleteError(error)
      if (!code) {
        toast.error('Failed to delete account')
        return
      }
      toast.error(t(`bankAccounts.deleteErrors.${code}`, { defaultValue: message ?? 'Failed to delete account' }))
      if (code === AccountDeleteErrorCode.NotFound) {
        // Someone else already deleted it: the grid is stale and there is nothing left to confirm.
        void queryClient.invalidateQueries({ queryKey: ['Accounts'] })
        onOpenChange(false)
      }
    }
  }

  return (
    <DeleteConfirmation
      open={account !== null}
      onOpenChange={onOpenChange}
      title="Are you sure you want to delete this account?"
      description="The account and its information will be permanently erased. This process cannot be undone."
      isDeleting={deleteMutation.isPending}
      onConfirm={() => void handleConfirm()}
    />
  )
}
